/**
 * RP-32.1 — why a Suggested Resource was named.
 *
 * The field is labelled **Suggested Resource** everywhere. "Requested Resource" is a
 * tooltip word only (spec §3: Suggested / Staged / Assignment are never interchangeable,
 * and "requested" would imply an obligation the plan does not carry).
 *
 * The reason exists so the RM can tell which suggestions are fixed: a Client Requested name
 * is a client conversation to change, the others are staffing calls.
 */

import type { SuggestedResourceReason } from "@/schemas/resource-plans";

export const SUGGESTED_RESOURCE_REASON_LABELS: Record<SuggestedResourceReason, string> = {
  ClientRequested: "Client Requested",
  PitchedToClient: "Pitched to Client",
  Continuity: "Continuity",
  Other: "Other",
};

/** One readable line — "Client Requested" or "Other: worked the 2024 pilot". */
export function describeSuggestedResourceReason(
  reason: SuggestedResourceReason | null | undefined,
  note: string | null | undefined,
): string | null {
  if (reason === null || reason === undefined) return null;
  const label = SUGGESTED_RESOURCE_REASON_LABELS[reason];
  const trimmed = note?.trim() ?? "";
  return trimmed.length > 0 ? `${label}: ${trimmed}` : label;
}

export interface ReasonState {
  reason: SuggestedResourceReason | null;
  note: string | null;
}

export class ReasonInputError extends Error {}

/**
 * Pure: the reason a Plan Line holds after a Suggested Resource write.
 *
 * - Clearing the Resource clears the reason.
 * - Naming a DIFFERENT Resource starts from no reason unless one is sent with it — a
 *   reason is about a Resource, so it never silently transfers to their replacement.
 * - Same Resource, nothing sent: keep what is there.
 * - A note without a reason qualifies the existing reason, or is refused.
 *
 * Throws {@link ReasonInputError} for a combination the CHECK constraints would reject,
 * so the caller can answer 422 instead of surfacing a database error.
 */
export function nextSuggestedResourceReason(input: {
  current: ReasonState & { resourceId: string | null };
  resourceId: string | null;
  reason: SuggestedResourceReason | null | undefined;
  note: string | null | undefined;
}): ReasonState {
  if (input.resourceId === null) return { reason: null, note: null };

  const sentNote =
    input.note === undefined ? undefined : input.note === null ? null : input.note.trim() || null;
  const sameResource = input.resourceId === input.current.resourceId;

  let reason: SuggestedResourceReason | null;
  if (input.reason !== undefined) reason = input.reason;
  else reason = sameResource ? input.current.reason : null;

  let note: string | null;
  if (reason === null) note = null;
  else if (sentNote !== undefined) note = sentNote;
  else if (input.reason !== undefined) note = null;
  else note = sameResource ? input.current.note : null;

  if (reason === null && sentNote !== undefined && sentNote !== null) {
    throw new ReasonInputError("Pick a reason before adding a note.");
  }
  if (reason === "Other" && note === null) {
    throw new ReasonInputError("Say why when the reason is Other.");
  }
  return { reason, note };
}
